/**
 * Shared bits of the Discord verification flow.
 *
 * The "intent" is how /verify-discord survives a cold start. Someone arriving
 * from the Discord server with no account at all goes through SSO, and
 * `app/sso/callback/page.tsx:35` deliberately drops `next` in favour of
 * /onboarding when the profile is incomplete. Rather than fight that redirect,
 * we carry the *intent* alongside it: onboarding's Discord step and the profile
 * card both read the flag and show "Return to Discord" on success.
 *
 * Every storage access is wrapped: a private window, cleared site data or a
 * blocked-storage policy must degrade to "no return button", never to a broken
 * verification.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { fetchWithAuth } from "@/lib/auth";

const INTENT_KEY = "discord_verify_intent";
const INTENT_TTL_MS = 60 * 60 * 1000; // one hour — long enough for SSO + onboarding

export interface DiscordConfig {
    configured: boolean;
    invite_url: string | null;
    return_url: string | null;
    /** The server itself — the standing "Open CMT Discord server" button. */
    server_url?: string | null;
    /** "Back to myCMT", for members who came from the myCMT button (`?from=mycmt`). */
    mycmt_url?: string | null;
}

/** Mark that this browser started at the Discord server's verify link. */
export function setVerifyIntent(): void {
    try {
        localStorage.setItem(INTENT_KEY, String(Date.now()));
    } catch {
        /* storage unavailable — the flow still completes, just without the deep link */
    }
}

/** True when the session began at /verify-discord and hasn't gone stale. */
export function hasVerifyIntent(): boolean {
    try {
        const raw = localStorage.getItem(INTENT_KEY);
        if (!raw) return false;
        const started = Number(raw);
        if (!Number.isFinite(started) || Date.now() - started > INTENT_TTL_MS) {
            localStorage.removeItem(INTENT_KEY);
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

export function clearVerifyIntent(): void {
    try {
        localStorage.removeItem(INTENT_KEY);
    } catch {
        /* nothing to clean up */
    }
}

/**
 * Public config — guild deep link and invite. Unauthenticated, so this works on
 * the cold path before anyone has signed in. Returns nulls rather than throwing:
 * a missing deep link hides a button, it does not break the page.
 */
export async function fetchDiscordConfig(): Promise<DiscordConfig> {
    try {
        const res = await fetch("/api/v1/auth/discord/config");
        if (!res.ok) throw new Error(String(res.status));
        return (await res.json()) as DiscordConfig;
    } catch {
        return { configured: false, invite_url: null, return_url: null };
    }
}

// Shared across every entry point on a page (top bar, dropdown, home card,
// profile card) so they cost one request, not four. Only a successful answer is
// kept — a transient failure must not hide the button for the whole session.
let configPromise: Promise<DiscordConfig> | null = null;

function cachedDiscordConfig(): Promise<DiscordConfig> {
    if (!configPromise) {
        configPromise = fetch("/api/v1/auth/discord/config")
            .then((res) => {
                if (!res.ok) throw new Error(String(res.status));
                return res.json() as Promise<DiscordConfig>;
            })
            .catch(() => {
                configPromise = null;
                return { configured: false, invite_url: null, return_url: null };
            });
    }
    return configPromise;
}

/**
 * Null while loading. Entry points render nothing until `configured` is known
 * to be true: prod carries no DISCORD_OAUTH_* keys until rollout, and a button
 * there would send members to a Discord authorize page with an empty client_id.
 */
export function useDiscordConfig(): DiscordConfig | null {
    const [config, setConfig] = useState<DiscordConfig | null>(null);
    useEffect(() => {
        let alive = true;
        void cachedDiscordConfig().then((c) => {
            if (alive) setConfig(c);
        });
        return () => {
            alive = false;
        };
    }, []);
    return config;
}

/** Where every in-Hub entry point sends the member back to: the profile card owns the result banner. */
export const DISCORD_RETURN_PATH = "/dashboard?section=profile&tab=contact";

/**
 * Where a link started from inside the Hub lands, in the tab it opened.
 * /verify-discord already renders every outcome; `done=1` tells it that it is
 * the far end of a new-tab hop, so it announces the result to the tab that
 * started it and says "you can close this tab".
 */
export const DISCORD_NEW_TAB_RETURN = "/verify-discord?done=1";

/**
 * Start the OAuth hop. It has to be a navigation, not a fetch: /discord/link
 * answers with a 302 to discord.com that fetch can't follow.
 *
 * `newTab` keeps the Hub open behind the consent screen — members were losing
 * their place. `window.open` runs inside the click handler, so popup blockers
 * allow it; if one refuses anyway, fall back to the same tab rather than do
 * nothing.
 */
export function startDiscordLink(next: string, opts: { newTab?: boolean } = {}): void {
    const dest = opts.newTab ? DISCORD_NEW_TAB_RETURN : next;
    const url = `/api/v1/auth/discord/link?next=${encodeURIComponent(dest)}`;
    if (opts.newTab) {
        const w = window.open(url, "_blank");
        if (w) return;
    }
    window.location.href = url;
}

// ── Cross-tab result ────────────────────────────────────────────────
// The OAuth hop finishes in the tab it opened. This carries the outcome back
// to the tab that started it, so its card flips to "Connected" at once. The
// WorkspaceContext's visibilitychange re-read is the fallback wherever
// BroadcastChannel is unavailable — every use here is wrapped.

const CHANNEL = "cmt-discord";

export type DiscordLinkEvent =
    | { type: "linked"; roles: string }
    | { type: "error"; code: string };

export function announceDiscordLink(event: DiscordLinkEvent): void {
    try {
        const ch = new BroadcastChannel(CHANNEL);
        ch.postMessage(event);
        ch.close();
    } catch {
        /* no BroadcastChannel — the other tab catches up on focus */
    }
}

/** Subscribe to link outcomes announced by another tab. */
export function useDiscordLinkEvents(cb: (event: DiscordLinkEvent) => void): void {
    const cbRef = useRef(cb);
    useEffect(() => {
        cbRef.current = cb;
    });
    useEffect(() => {
        let ch: BroadcastChannel | null = null;
        try {
            ch = new BroadcastChannel(CHANNEL);
            ch.onmessage = (e: MessageEvent<DiscordLinkEvent>) => cbRef.current(e.data);
        } catch {
            return;
        }
        return () => ch?.close();
    }, []);
}

// ── Guild membership ────────────────────────────────────────────────
// Whether a linked member is actually in the server, and the "I've joined"
// re-check. One store for the page, so the top-bar pill, the home card and the
// profile card share a single POST /discord/refresh rather than each firing
// their own.

export interface DiscordMembership {
    linked: boolean;
    /** "granted" | "not_in_guild" | "failed" | "skipped" */
    roles: string | null;
    /** null = unknown (bot not configured, or Discord failed) — never "absent". */
    in_guild: boolean | null;
    username?: string | null;
    invite_url?: string | null;
    server_url?: string | null;
}

interface MembershipState {
    data: DiscordMembership | null;
    checking: boolean;
    /** True while polling for the member to arrive in the server. */
    watching: boolean;
    /** Set once a watch ends in "granted" — drives the "Roles assigned" line. */
    justGranted: boolean;
}

const INITIAL: MembershipState = { data: null, checking: false, watching: false, justGranted: false };
let mState: MembershipState = INITIAL;
let mLoadedFor: string | null = null;
const mListeners = new Set<() => void>();

function setMembership(patch: Partial<MembershipState>): void {
    mState = { ...mState, ...patch };
    mListeners.forEach((l) => l());
}

const WATCH_INTERVAL_MS = 5000;
const WATCH_MAX_MS = 3 * 60 * 1000;
let watchTimer: ReturnType<typeof setInterval> | null = null;
let watchStarted = 0;
let inFlight: Promise<DiscordMembership | null> | null = null;

function stopWatching(): void {
    if (watchTimer) clearInterval(watchTimer);
    watchTimer = null;
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
    if (mState.watching) setMembership({ watching: false });
}

function onVisible(): void {
    if (document.visibilityState === "visible") void checkDiscordMembership();
}

/** POST /discord/refresh: grant roles now if they're in the server, and report whether they are. */
export function checkDiscordMembership(): Promise<DiscordMembership | null> {
    if (inFlight) return inFlight;
    setMembership({ checking: true });
    inFlight = fetchWithAuth("/api/v1/auth/discord/refresh", { method: "POST" })
        .then(async (res) => {
            if (!res.ok) throw new Error(String(res.status));
            const data = (await res.json()) as DiscordMembership;
            const granted = data.roles === "granted";
            setMembership({
                data,
                checking: false,
                justGranted: mState.justGranted || (mState.watching && granted),
            });
            if (granted) stopWatching();
            return data;
        })
        .catch(() => {
            setMembership({ checking: false });
            return null;
        })
        .finally(() => {
            inFlight = null;
        });
    return inFlight;
}

/**
 * Poll while the member joins the server: every 5s for up to 3 minutes while
 * this tab is visible, plus once whenever they come back to it (the invite
 * opens Discord elsewhere, so "back to this tab" is the likeliest moment they
 * have finished). Stops the moment roles are granted.
 */
export function watchForDiscordJoin(): void {
    stopWatching();
    watchStarted = Date.now();
    setMembership({ watching: true, justGranted: false });
    document.addEventListener("visibilitychange", onVisible);
    watchTimer = setInterval(() => {
        if (Date.now() - watchStarted > WATCH_MAX_MS) {
            stopWatching();
            return;
        }
        if (document.visibilityState === "visible") void checkDiscordMembership();
    }, WATCH_INTERVAL_MS);
}

/** Forget everything — after an unlink, or when the signed-in user changes. */
export function resetDiscordMembership(): void {
    stopWatching();
    mLoadedFor = null;
    mState = INITIAL;
    mListeners.forEach((l) => l());
}

function subscribe(l: () => void): () => void {
    mListeners.add(l);
    return () => {
        mListeners.delete(l);
    };
}

const getSnapshot = () => mState;
const getServerSnapshot = () => INITIAL;

/**
 * Membership for the signed-in member. Checks once per user per page load, the
 * first time any consumer mounts with a linked account; later mounts share the
 * answer. Pass `user?.id`, never the user object (see CLAUDE.md — the profile
 * is re-read on every tab focus).
 */
export function useDiscordMembership(userId: string | null | undefined, linked: boolean): MembershipState {
    const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    useEffect(() => {
        if (!userId || !linked) return;
        if (mLoadedFor === userId) return;
        if (mLoadedFor !== null) resetDiscordMembership();
        mLoadedFor = userId;
        void checkDiscordMembership();
    }, [userId, linked]);
    return state;
}
